import { Button, Form, Input, Select, Spin } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import routes from '@/constants/routes'
import { Customer, WashingMethod } from '@/types'
import { GetCustomerResponse } from '@/types/apis/customers/GetCustomerResponse'
import { UpdateCustomerRequest } from '@/types/apis/customers/UpdateCustomerRequest'

import { SpaceCustom } from '../../style'
import { SelectWrapper } from '../styles'

type Props = {
  customerId: number
  updateLoading: boolean
  getCustomerResponse: GetCustomerResponse | undefined
  washingMethods: WashingMethod[] | undefined
  getLoading: boolean
  onSubmit: (formValues: UpdateCustomerRequest) => void
  onDelete: (id: Customer['id']) => void
}

export type UpdateCustomerFormValue = {
  id: number
  code?: string
  shortName: string
  name: string
  washingMethodIds: number[]
}

const UpdateCustomerForm = ({ getCustomerResponse, washingMethods, updateLoading, getLoading, onSubmit }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<UpdateCustomerFormValue>()

  useEffect(() => {
    if (getCustomerResponse) {
      form.setFieldsValue({
        id: getCustomerResponse.id,
        code: getCustomerResponse.code,
        name: getCustomerResponse.name,
        shortName: getCustomerResponse.shortName,
        washingMethodIds: getCustomerResponse.washingMethodCustomers.map((wm) => wm.washingMethodId),
      })
    }
  }, [form, getCustomerResponse])

  return (
    <Spin spinning={getLoading}>
      <Form layout="vertical" onFinish={onSubmit} form={form}>
        <Form.Item label={t('components.pages.customers.update.form.code')} name="code">
          <Input />
        </Form.Item>

        <Form.Item
          label={t('components.pages.customers.update.form.shortName')}
          name="shortName"
          rules={[{ required: true, message: t('components.pages.customers.update.form.requireShortName') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t('components.pages.customers.update.form.name')}
          name="name"
          rules={[{ required: true, message: t('components.pages.customers.update.form.requireName') }]}
        >
          <Input />
        </Form.Item>

        <SelectWrapper>
          <Form.Item label={t('components.pages.customers.update.form.method')} name="washingMethodIds">
            <Select allowClear mode="multiple">
              {washingMethods?.map((method) => (
                <Select.Option key={method.id} value={method.id}>
                  {method.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SelectWrapper>
        <Form.Item>
          <SpaceCustom>
            <Button
              onClick={() => {
                navigate(routes.customers.path)
              }}
            >
              {t('components.pages.customers.update.form.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              {t('components.pages.customers.update.form.update')}
            </Button>
          </SpaceCustom>
        </Form.Item>
      </Form>
    </Spin>
  )
}

UpdateCustomerForm.displayName = 'UpdateCustomerForm'
export default UpdateCustomerForm
